// Statistiche di prima parte di immaginacasa.com (#652).
//
// Invia al collector `POST /api/collect` (rewrite Firebase Hosting → function
// `collect`, europe-west1, repo newarc-cloud-functions) gli eventi descritti
// nella spec newarc-platform
// docs/superpowers/specs/2026-09-08-first-party-site-stats-design.md (§4).
//
// Perché sta FUORI dal gating del consenso (window.IcConsent / consentManager):
// non usa cookie, localStorage, sessionStorage né alcun identificatore
// persistente o fingerprinting. L'unico id è `pageload_id`, generato in
// memoria a ogni caricamento della pagina e perso alla chiusura: non permette
// di riconoscere il visitatore tra due visite. Il server non conserva l'IP
// (hash giornaliero non reversibile, spec §5). Rientra quindi nelle
// statistiche aggregate di prima parte che le Linee guida cookie del Garante
// (10 giugno 2021) assimilano ai cookie tecnici. Nessun dato personale viene
// inviato. La cookie policy lo documenta (src/views/CookiePolicy.vue).
//
// Contratto del collector (snake_case; newarc-cloud-functions
// functions/src/siteStats/validate.ts):
//   { events: [{ event, pageload_id, ad_id?, agency_id?, surface?, position?,
//                contact_type?, path? }] }
//   - max 20 eventi e 32 KB per richiesta; Content-Type text/plain o JSON;
//   - pageload_id /^[A-Za-z0-9-]{8,64}$/; ad_id, agency_id /^[A-Za-z0-9_-]{1,128}$/;
//   - path /^\/[A-Za-z0-9_\-/:.]{0,200}$/ (template di route, mai la query);
//   - surface ∈ list|map|home|similar; position intero 1..500;
//   - contact_type ∈ visit|videocall|designer|phone|email|form;
//   - risposte: 204 ok, 400/413 input non valido, 405, 500 errore server.
//   Gli eventi non validi vengono scartati singolarmente dal server.
//
// Trasporto: coda in memoria svuotata 2 s dopo il primo evento, subito a 20
// eventi e su pagehide / visibilitychange→hidden. navigator.sendBeacon quando
// disponibile, altrimenti fetch keepalive con UN solo retry su errore di rete
// (mai su risposta 4xx/5xx). Nessun errore viene mai propagato al chiamante.
//
// Disattivato (no-op totale) fuori dal browser, su localhost/127.0.0.1 (in
// `npm run serve` non esiste il rewrite: /api/collect risponderebbe 404) e con
// VUE_APP_STATS_DISABLED=true.
//
// `client_ts` non viene inviato: il server usa il proprio `ts`.

export const ENDPOINT = '/api/collect';
export const FLUSH_INTERVAL_MS = 2000;
export const MAX_BATCH = 20;
export const RETRY_DELAY_MS = 2000;
// Path fuori contratto: l'aggregatore conta comunque in /_other i template
// non elencati in KNOWN_PAGE_PATHS, quindi la vista non va persa.
export const OTHER_PATH = '/_other';

const PATH_RE = /^\/[A-Za-z0-9_\-/:.]{0,200}$/;
const MAX_POSITION = 500;
const MAX_IMPRESSIONS_TRACKED = 5000;
const DISABLED_HOSTS = ['localhost', '127.0.0.1'];

const state = {
  queue: [],
  timer: null,
  pageloadId: null,
  impressions: new Set(),
  listenersBound: false,
  hostnameOverride: null,
  // Handle dei setTimeout dei retry di rete in corso, per poterli cancellare
  // da __resetForTests (senza, un retry pendente da un test precedente
  // potrebbe far scattare una fetch inattesa in un test successivo).
  retryTimers: []
};

export function isStatsEnabled () {
  if (typeof window === 'undefined') return false;
  if (process.env.VUE_APP_STATS_DISABLED === 'true') return false;
  const hostname = state.hostnameOverride ||
    (window.location && window.location.hostname) ||
    '';
  return !DISABLED_HOSTS.includes(hostname);
}

function randomId () {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (e) {
    // si passa al fallback
  }
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 24; i++) id += alphabet[Math.floor(Math.random() * alphabet.length)];
  return id;
}

/** Id del caricamento pagina, solo in memoria; generato al primo uso. */
export function getPageloadId () {
  if (!state.pageloadId) state.pageloadId = randomId();
  return state.pageloadId;
}

function onPageHide () {
  flush();
}

function onVisibilityChange () {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') flush();
}

function bindLifecycle () {
  if (state.listenersBound || typeof window === 'undefined') return;
  state.listenersBound = true;
  window.addEventListener('pagehide', onPageHide);
  document.addEventListener('visibilitychange', onVisibilityChange);
}

function unbindLifecycle () {
  if (!state.listenersBound) return;
  state.listenersBound = false;
  window.removeEventListener('pagehide', onPageHide);
  document.removeEventListener('visibilitychange', onVisibilityChange);
}

/** Come trackEvent di analytics.js: via undefined / null / '' (0 e false restano). */
function clean (fields) {
  const out = {};
  for (const [k, v] of Object.entries(fields)) {
    if (v !== undefined && v !== null && v !== '') out[k] = v;
  }
  return out;
}

/** Accoda un evento. Ritorna true se accodato (false quando disattivato o in errore). */
function enqueue (event, fields = {}) {
  if (!isStatsEnabled()) return false;
  try {
    state.queue.push({ event, pageload_id: getPageloadId(), ...clean(fields) });
    bindLifecycle();
    if (state.queue.length >= MAX_BATCH) {
      flush();
    } else if (!state.timer) {
      state.timer = setTimeout(flush, FLUSH_INTERVAL_MS);
    }
    return true;
  } catch (e) {
    console.warn('[stats] enqueue failed', e);
    return false;
  }
}

function sendWithFetch (json, isRetry) {
  if (typeof fetch !== 'function') return;
  let request;
  try {
    request = fetch(ENDPOINT, {
      method: 'POST',
      body: json,
      headers: { 'Content-Type': 'text/plain' },
      keepalive: true
    });
  } catch (e) {
    request = Promise.reject(e);
  }
  // Una risposta qualsiasi (anche 4xx/5xx) chiude il tentativo: si ritenta
  // solo quando la promise rigetta, cioè su errore di rete, e una volta sola.
  Promise.resolve(request).then(
    () => {},
    (e) => {
      if (isRetry) {
        console.warn('[stats] send failed', e);
        return;
      }
      const handle = setTimeout(() => {
        const idx = state.retryTimers.indexOf(handle);
        if (idx !== -1) state.retryTimers.splice(idx, 1);
        sendWithFetch(json, true);
      }, RETRY_DELAY_MS);
      state.retryTimers.push(handle);
    }
  );
}

function send (json) {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([json], { type: 'text/plain' });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
  } catch (e) {
    // sendBeacon assente o rifiutato: si passa a fetch.
  }
  sendWithFetch(json, false);
}

/** Spedisce subito tutto ciò che è in coda, a lotti di MAX_BATCH. */
export function flush () {
  if (state.timer) {
    clearTimeout(state.timer);
    state.timer = null;
  }
  try {
    while (state.queue.length) {
      const batch = state.queue.splice(0, MAX_BATCH);
      send(JSON.stringify({ events: batch }));
    }
  } catch (e) {
    console.warn('[stats] flush failed', e);
  }
}

/**
 * Template della route (es. /annunci/:docId) dal record matchato: mai il path
 * concreto. Gli alias (/annunci/:docId) tornano al record canonico; le route
 * figlie (/configure/...) usano il proprio record; senza record matchato
 * (404, catch-all assente) o con un record senza path né aliasOf va a
 * OTHER_PATH.
 */
export function routeTemplate (to) {
  const matched = to && Array.isArray(to.matched) ? to.matched : [];
  const rec = matched[matched.length - 1];
  if (!rec) return OTHER_PATH;
  return (rec.aliasOf && rec.aliasOf.path) || rec.path || OTHER_PATH;
}

function normalizePosition (position) {
  const n = Number(position);
  if (!Number.isInteger(n) || n < 1) return 1;
  return Math.min(n, MAX_POSITION);
}

export function trackPageView (path) {
  const safe = typeof path === 'string' && PATH_RE.test(path) ? path : OTHER_PATH;
  enqueue('page_view', { path: safe });
}

export function trackAdView ({ adId, agencyId } = {}) {
  if (!adId) return;
  enqueue('ad_view', { ad_id: adId, agency_id: agencyId });
}

/** Una impression per annuncio per superficie per caricamento pagina. */
export function trackAdImpression ({ adId, agencyId, surface, position } = {}) {
  if (!adId || !surface) return;
  const key = `${surface}:${adId}`;
  if (state.impressions.has(key)) return;
  // Tetto di sicurezza: una SPA a lunga vita (nessun reload) potrebbe altrimenti
  // far crescere il set all'infinito. Oltre la soglia si riparte da zero: si
  // perde la dedup su impression molto vecchie, mai sui contatori del server.
  if (state.impressions.size >= MAX_IMPRESSIONS_TRACKED) state.impressions.clear();
  const queued = enqueue('ad_impression', {
    ad_id: adId,
    agency_id: agencyId,
    surface,
    position: normalizePosition(position)
  });
  if (queued) state.impressions.add(key);
}

export function trackAdClick ({ adId, agencyId, surface, position } = {}) {
  if (!adId || !surface) return;
  enqueue('ad_click', {
    ad_id: adId,
    agency_id: agencyId,
    surface,
    position: normalizePosition(position)
  });
}

export function trackAdSave ({ adId, agencyId } = {}) {
  if (!adId) return;
  enqueue('ad_save', { ad_id: adId, agency_id: agencyId });
}

export function trackAdUnsave ({ adId, agencyId } = {}) {
  if (!adId) return;
  enqueue('ad_unsave', { ad_id: adId, agency_id: agencyId });
}

export function trackAdContact ({ adId, agencyId, contactType } = {}) {
  if (!adId || !contactType) return;
  enqueue('ad_contact', { ad_id: adId, agency_id: agencyId, contact_type: contactType });
}

/** Solo test: forza l'hostname usato da isStatsEnabled (jsdom gira su localhost). */
export function __configureForTests ({ hostname } = {}) {
  state.hostnameOverride = hostname || null;
}

/** Solo test: riporta il servizio allo stato di un nuovo caricamento pagina. */
export function __resetForTests () {
  if (state.timer) clearTimeout(state.timer);
  state.timer = null;
  state.queue = [];
  state.pageloadId = null;
  state.impressions = new Set();
  state.hostnameOverride = null;
  for (const handle of state.retryTimers) clearTimeout(handle);
  state.retryTimers = [];
  unbindLifecycle();
}
